import { useState, useEffect } from "react";
import styled from "styled-components";
import LeftNav from "../../components/organisms/LeftNav";
import appConfig from "../../lib/appConfig";
import colours from "../../lib/colours";
import {
  Patch,
  RevegetationArea,
  Tree,
  nvrToolModes,
  nvrTools,
} from "../../lib/types";
import { offsetStages } from "../../lib/workflow";
import DataPanel from "../Other/DataPanel";
import MapNativeVeg from "../Stages/MapNativeVeg";
import SummaryReport from "../Stages/SummaryReport";
import { IState } from "../../context/featuresReducer";
import {
  useFeaturesContext,
  useNvrToolModeContext,
} from "../../context/ContextProvider";
import { useDebouncedEffect } from "../../utils/useDebouncedEffect";
import { fetchOffsetSummary } from "../../utils/requests";

const Container = styled.div`
  display: flex;
  height: 100%;
`;

const DataPanelContainer = styled.div`
  border-right: 1px solid ${colours.offWhite};
  @media (max-width: ${appConfig.tabletWidth}px) {
    display: block !important;
  }
`;

function OffsetTool() {
  const [dataPanelOpen, setDataPanelOpen] = useState(true);
  const [stage, setStage] = useState<number>(0);
  const [maxEnabledStage, setMaxEnabledStage] = useState<number>(0);
  const state: IState = useFeaturesContext();
  const [showMappingStageFooter, setShowMappingStageFooter] =
    useState<boolean>(false);
  const [locationResults, setLocationResults] = useState<any>(undefined);
  const [offsetResults, setOffsetResults] = useState<any>();
  const [fetching, setFetching] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const {
    nvrToolMode,
    setNvrToolMode,
  }: { nvrToolMode: nvrToolModes; setNvrToolMode: any } =
    useNvrToolModeContext();

  const anyUnresolvedMappingIssues = state.treesList.some((x: string) => {
    return (state.features[x] as Tree).benchmarkCm === undefined;
  });

  useEffect(() => {
    setNvrToolMode(nvrTools.offset);
  }, [setNvrToolMode]);

  // update workflow progress
  useEffect(() => {
    const featuresList = state.patchesList
      .concat(state.treesList)
      .concat(state.revegetationAreasList);
    const passedStage1 = !anyUnresolvedMappingIssues && featuresList.length;
    if (!passedStage1) {
      setMaxEnabledStage(0);
      setShowMappingStageFooter(false);
      return;
    }
    setMaxEnabledStage(1);
    setShowMappingStageFooter(true);
  }, [
    anyUnresolvedMappingIssues,
    state,
    setMaxEnabledStage,
    setShowMappingStageFooter,
  ]);

  // regenerate summary when state changes
  // debounce the effect so that we avoid excessive amount of requests
  useDebouncedEffect(
    async () => {
      const emptySummary =
        Object.keys(state.features).length === 0 || anyUnresolvedMappingIssues;
      if (emptySummary) {
        setLocationResults(undefined);
        setOffsetResults(undefined);
        setRequestError(false);
        return;
      }
      setFetching(true);
      const patches = state.patchesList.map((x) => state.features[x] as Patch);
      const trees = state.treesList.map((x) => state.features[x] as Tree);
      const revegatationAreas = state.revegetationAreasList.map(
        (x) => state.features[x] as RevegetationArea
      );
      await fetchOffsetSummary(patches, trees, revegatationAreas)
        .then((results: any[]) => {
          setLocationResults(results[0]);
          setOffsetResults(results[1]);
          setRequestError(false);
          setFetching(false);
        })
        .catch((e) => {
          console.error(e);
          setLocationResults(undefined);
          setOffsetResults(undefined);
          setRequestError(true);
          setFetching(false);
        });
    },
    [state, anyUnresolvedMappingIssues],
    2000
  );

  const renderDataPanelComponent = () => {
    const restProps = {
      close: () => setDataPanelOpen(false),
      setMaxEnabledStage,
      setStage,
    };
    switch (stage) {
      case 0:
        return (
          <MapNativeVeg
            mode={nvrToolMode}
            showFooter={showMappingStageFooter}
            {...restProps}
          />
        );
      case 1:
        return (
          <SummaryReport
            mode={nvrToolMode}
            locationResults={locationResults}
            fetching={fetching}
            requestError={requestError}
            offsetResults={offsetResults}
            {...restProps}
          />
        );
    }
  };

  return (
    <Container>
      <LeftNav
        title="Offset Tool"
        stages={offsetStages}
        stage={stage}
        maxEnabledStage={maxEnabledStage}
        setStage={setStage}
        dataPanelOpen={dataPanelOpen}
        openDataPanel={() => setDataPanelOpen(true)}
      />
      <DataPanelContainer style={{ display: dataPanelOpen ? "block" : "none" }}>
        <DataPanel>{renderDataPanelComponent()}</DataPanel>
      </DataPanelContainer>
    </Container>
  );
}

export default OffsetTool;
